import { useCallback, useEffect } from 'react';
import { FinancialDocumentAllocation } from '../../../../../../models/IncomingInvoices';
import { useIncomingInvoiceDrawerContext } from '../incomingInvoiceComponents/IncomingInvoiceContextProvider';

interface UseAllocationStateProps {
  initialAllocations: Partial<FinancialDocumentAllocation>[];
  financialDocumentId: string;
}

export const useAllocationState = ({
  initialAllocations,
  financialDocumentId,
}: UseAllocationStateProps) => {
  const { invoiceAllocations, setInvoiceAllocations, setIsDirtyAllocations } =
    useIncomingInvoiceDrawerContext();

  useEffect(() => {
    setInvoiceAllocations([...initialAllocations]);
    setIsDirtyAllocations(false);
  }, [initialAllocations, setInvoiceAllocations, setIsDirtyAllocations]);

  const addAllocation = useCallback(
    (rest?: number) => {
      const newAlloc: Partial<FinancialDocumentAllocation> = {
        financialDocumentId: financialDocumentId,
        projectId: undefined,
        amount: rest ?? 0,
      };
      setInvoiceAllocations([...invoiceAllocations, newAlloc]);
      setIsDirtyAllocations(true);
    },
    [
      financialDocumentId,
      invoiceAllocations,
      setInvoiceAllocations,
      setIsDirtyAllocations,
    ]
  );

  const updateAllocation = useCallback(
    (index: number, updated: Partial<FinancialDocumentAllocation>) => {
      setInvoiceAllocations(
        invoiceAllocations.map((p, i) =>
          i === index
            ? {
                ...p,
                ...updated,
                financialDocumentAllocationId: p.financialDocumentAllocationId,
              }
            : p
        )
      );
      setIsDirtyAllocations(true);
    },
    [invoiceAllocations, setInvoiceAllocations, setIsDirtyAllocations]
  );

  const removeAllocationLocally = useCallback(
    (index: number) => {
      const allocationToRemove = invoiceAllocations[index];
      const updatedAllocations = invoiceAllocations.filter(
        (_, i) => i !== index
      );

      setInvoiceAllocations(updatedAllocations);

      // If the allocation was never saved (no ID) and after removal we're back to initial state, reset dirty flag
      if (!allocationToRemove?.financialDocumentAllocationId) {
        const isBackToInitial =
          updatedAllocations.length === initialAllocations.length &&
          updatedAllocations.every((alloc, idx) => {
            const initial = initialAllocations[idx];
            return (
              alloc.financialDocumentAllocationId ===
                initial.financialDocumentAllocationId &&
              alloc.projectId === initial.projectId &&
              alloc.projectPhaseId === initial.projectPhaseId &&
              alloc.amount === initial.amount
            );
          });

        if (isBackToInitial) {
          setIsDirtyAllocations(false);
        }
      }
    },
    [
      invoiceAllocations,
      setInvoiceAllocations,
      initialAllocations,
      setIsDirtyAllocations,
    ]
  );

  const resetToOriginal = useCallback(() => {
    setInvoiceAllocations([...initialAllocations]);
    setIsDirtyAllocations(false);
  }, [initialAllocations, setInvoiceAllocations, setIsDirtyAllocations]);

  const markAsSaved = useCallback(() => {
    setIsDirtyAllocations(false);
  }, [setIsDirtyAllocations]);

  return {
    invoiceAllocations,
    addAllocation,
    updateAllocation,
    removeAllocationLocally,
    resetToOriginal,
    markAsSaved,
  };
};
